import api from "./client";

export const companies = {
  getAll: () => api.get("/companies").then((res) => res.data),
  getProfile: () => api.get("/companies/profile").then((res) => res.data),
  updateProfile: (data: any) =>
    api.patch("/companies/profile", data).then((res) => res.data),
  getShippingConfig: () =>
    api.get("/companies/shipping-config").then((res) => res.data),
  updateShippingConfig: (data: any) =>
    api.patch("/companies/shipping-config", data).then((res) => res.data),
  uploadLogo: (formData: FormData) =>
    api
      .post("/companies/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data),
  /** The company brochure printed with every parcel. Replaces any existing one. */
  uploadBrochure: (formData: FormData) =>
    api
      .post("/companies/brochure", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data),
  removeBrochure: () => api.delete("/companies/brochure").then((res) => res.data),
  /** The top-level categories, each with its own brochure or null. */
  getCategoryBrochures: () =>
    api.get("/companies/category-brochures").then((res) => res.data),
  /** Upload one category's brochure. Replaces its existing one; returns the full list. */
  uploadCategoryBrochure: (categoryId: number, formData: FormData) =>
    api
      .post(`/companies/category-brochures/${categoryId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data),
  removeCategoryBrochure: (categoryId: number) =>
    api.delete(`/companies/category-brochures/${categoryId}`).then((res) => res.data),
  getApiKeys: () => api.get("/companies/api-keys").then((res) => res.data),
  generateApiKey: () => api.post("/companies/api-keys").then((res) => res.data),
  revokeApiKey: (keyId: string) =>
    api.post(`/companies/api-keys/${keyId}/revoke`).then((res) => res.data),
  updateApiKey: (keyId: string, data: any) =>
    api.patch(`/companies/api-keys/${keyId}`, data).then((res) => res.data),
  getAutoBatchLogs: (page: number = 1, limit: number = 10, search: string = "", date: string = "") => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: search,
    });
    if (date) params.set('date', date);
    return api.get(`/companies/auto-batch/logs?${params}`).then((res) => res.data);
  },
  resendAutoBatchEmail: (
    batchId: string,
    startOrderIncrementId: string,
    endOrderIncrementId: string,
  ) =>
    api
      .post(`/companies/auto-batch/logs/${encodeURIComponent(batchId)}/resend-email`, {
        startOrderIncrementId,
        endOrderIncrementId,
      })
      .then((res) => res.data),

  // Pre Auto-Batch Report — packaging dry run, creates no labels or shipments.
  // startOrderNumber is optional — omit to continue from the saved cursor.
  runPreAutoBatch: (startOrderNumber?: string) =>
    api
      .post('/companies/auto-batch/pre-report/run', startOrderNumber ? { startOrderNumber } : {})
      .then((res) => res.data),
  getPreAutoBatchStatus: () =>
    api.get('/companies/auto-batch/pre-report/status').then((res) => res.data),
  getPreAutoBatchLogs: (page: number = 1, limit: number = 10, date: string = '') =>
    api
      .get(`/companies/auto-batch/pre-report/logs?page=${page}&limit=${limit}&date=${date}`)
      .then((res) => res.data),

  // Box Management
  getBoxes: (page: number = 1, limit: number = 10, filters: any = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: filters.search || '',
      type: filters.type || '',
      status: filters.status || ''
    });
    // Column sort ('cost' | 'availableQty', 'asc' | 'desc'), across every page.
    if (filters.sortBy && filters.sortDir) {
      params.set('sortBy', filters.sortBy);
      params.set('sortDir', filters.sortDir);
    }
    return api.get(`/boxes?${params}`).then((res) => res.data);
  },
  addBox: (data: any) => api.post("/boxes", data).then((res) => res.data),
  /** Default name for a new box/skid/mailer, continuing the company's sequence. */
  nextBoxName: (params: {
    type: string;
    mailerType?: string;
    length?: number;
    width?: number;
    height?: number;
  }): Promise<{ name: string }> => {
    const qs = new URLSearchParams({ type: params.type });
    if (params.mailerType) qs.set("mailerType", params.mailerType);
    if (params.length) qs.set("length", String(params.length));
    if (params.width) qs.set("width", String(params.width));
    if (params.height) qs.set("height", String(params.height));
    return api.get(`/boxes/next-name?${qs}`).then((res) => res.data);
  },
  updateBox: (id: string, data: any) =>
    api.put(`/boxes/${id}`, data).then((res) => res.data),
  deleteBox: (id: string) =>
    api.delete(`/boxes/${id}`).then((res) => res.data),
  exportBoxesCsv: () =>
    api.get("/boxes/export-csv").then((res) => res.data),
  importBoxesCsv: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api
      .post("/boxes/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  // Packaging Logs
  getPackagingLogs: (page: number = 1, limit: number = 20, filters: any = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters.orderId) params.set('orderId', filters.orderId);
    if (filters.orderNumber) params.set('orderNumber', filters.orderNumber);
    if (filters.purpose && filters.purpose !== 'ALL') params.set('purpose', filters.purpose);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.carrier) params.set('carrier', filters.carrier);
    if (filters.sku) params.set('sku', filters.sku);
    if (filters.selection) params.set('selection', filters.selection);
    if (filters.traceId) params.set('traceId', filters.traceId);
    return api.get(`/packaging-logs?${params}`).then((res) => res.data);
  },
  getPackagingLogCarriers: (): Promise<string[]> =>
    api.get('/packaging-logs/carriers').then((res) => res.data),
  getPackagingLog: (id: string) =>
    api.get(`/packaging-logs/${id}`).then((res) => res.data),
  deletePackagingLog: (id: string) =>
    api.delete(`/packaging-logs/${id}`).then((res) => res.data),
  getSmtpLogs: (page: number = 1, limit: number = 50, search?: string, status?: string) => {
    let url = `/companies/smtp-logs?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    return api.get(url).then((res) => res.data);
  },
  deleteAllSmtpLogs: () => api.delete('/companies/smtp-logs').then((res) => res.data),
  getSmsLogs: (page: number = 1, limit: number = 50, search?: string, status?: string) => {
    let url = `/companies/sms-logs?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    return api.get(url).then((res) => res.data);
  },
  deleteAllSmsLogs: () => api.delete('/companies/sms-logs').then((res) => res.data),
  getCronSchedules: (page: number = 1, limit: number = 50, search?: string, status?: string) => {
    let url = `/companies/cron-schedules?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    return api.get(url).then((res) => res.data);
  },
  deleteAllCronSchedules: () => api.delete('/companies/cron-schedules').then((res) => res.data),
  regenerateCronSchedules: () => api.post('/companies/cron-schedules/regenerate').then((res) => res.data),

  // Rate Request / Third-Party HTTP Logs
  getRateRequests: (page: number = 1, limit: number = 20, filters: any = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters.provider && filters.provider !== 'ALL') params.set('provider', filters.provider);
    if (filters.purpose && filters.purpose !== 'ALL') params.set('purpose', filters.purpose);
    if (filters.callType && filters.callType !== 'ALL') params.set('callType', filters.callType);
    if (filters.traceId) params.set('traceId', filters.traceId);
    if (filters.orderId) params.set('orderId', filters.orderId);
    if (filters.trackingNumber) params.set('trackingNumber', filters.trackingNumber);
    if (filters.customerEmail) params.set('customerEmail', filters.customerEmail);
    if (filters.zipCode) params.set('zipCode', filters.zipCode);
    if (filters.sku) params.set('sku', filters.sku);
    if (filters.requestType && filters.requestType !== 'ALL') params.set('requestType', filters.requestType);
    if (filters.responseStatus && filters.responseStatus !== 'ALL') params.set('responseStatus', filters.responseStatus);
    if (filters.search) params.set('search', filters.search);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    return api.get(`/rate-requests?${params}`).then((res) => res.data);
  },
  getRateRequestProviders: (): Promise<string[]> =>
    api.get('/rate-requests/providers').then((res) => res.data),
  getRateRequest: (id: string) =>
    api.get(`/rate-requests/${id}`).then((res) => res.data),
  deleteRateRequest: (id: string) =>
    api.delete(`/rate-requests/${id}`).then((res) => res.data),
  getGroupedRateRequestsReport: (filters: any = {}) => {
    const params = new URLSearchParams();
    if (filters.period) params.set('period', filters.period);
    if (filters.provider && filters.provider !== 'ALL') params.set('provider', filters.provider);
    if (filters.purpose && filters.purpose !== 'ALL') params.set('purpose', filters.purpose);
    if (filters.callType && filters.callType !== 'ALL') params.set('callType', filters.callType);
    if (filters.traceId) params.set('traceId', filters.traceId);
    if (filters.orderId) params.set('orderId', filters.orderId);
    if (filters.trackingNumber) params.set('trackingNumber', filters.trackingNumber);
    if (filters.customerEmail) params.set('customerEmail', filters.customerEmail);
    if (filters.zipCode) params.set('zipCode', filters.zipCode);
    if (filters.sku) params.set('sku', filters.sku);
    if (filters.requestType && filters.requestType !== 'ALL') params.set('requestType', filters.requestType);
    if (filters.responseStatus && filters.responseStatus !== 'ALL') params.set('responseStatus', filters.responseStatus);
    if (filters.search) params.set('search', filters.search);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    return api.get(`/rate-requests/report?${params}`).then((res) => res.data);
  },

  // Support Request
  submitSupportRequest: (data: {
    subject: string;
    message: string;
    category?: string;
    priority?: string;
    contactPhone?: string;
  }) => api.post('/companies/support', data).then((res) => res.data),
};


